/**
 * Pomocné funkce pro formátované poznámky (viz components/Shared/RichTextInput.vue).
 *
 * Poznámky se ukládají do stávajících textových sloupců jako omezená podmnožina
 * HTML. Protože obsah může pocházet i od zákazníka, musí každé zobrazení projít
 * přes sanitizeNoteHtml() - v aplikaci není žádné jiné důvěryhodné v-html.
 */

/** Značky, které v poznámce necháme projít. */
const ALLOWED_TAGS = [
  "B",
  "STRONG",
  "I",
  "EM",
  "U",
  "S",
  "A",
  "BR",
  "P",
  "DIV",
  "UL",
  "OL",
  "LI",
];

/** Značky, které zahodíme včetně obsahu (zbytek jen rozbalíme). */
const DROPPED_TAGS = [
  "SCRIPT",
  "STYLE",
  "IFRAME",
  "OBJECT",
  "EMBED",
  "LINK",
  "META",
  "FORM",
  "INPUT",
  "BUTTON",
  "TEXTAREA",
  "SELECT",
  "SVG",
  "MATH",
  "TEMPLATE",
  "NOSCRIPT",
];

/**
 * Zastaralé značky, které prohlížeč vyrobí přes execCommand, přepsané na jejich
 * dnešní podobu. Chrome dělá z přeškrtnutí <strike>, které v allowlistu není.
 */
const TAG_ALIASES: Record<string, string> = {
  STRIKE: "s",
  DEL: "s",
};

/** Schémata, která jsou v odkazu bezpečná. */
const ALLOWED_SCHEMES = ["http:", "https:", "mailto:"];

/** Zachytí holé URL a e-mailové adresy v textu. */
const AUTOLINK_PATTERN =
  /((?:https?:\/\/|www\.)[^\s<>"']+|[^\s<>"']+@[^\s<>"'@]+\.[a-z]{2,})/gi;

/** Interpunkce, která bývá za URL ve větě a do odkazu nepatří. */
const TRAILING_PUNCTUATION = /[.,;:!?)\]}>'"]+$/;

/** Značky, které v poznámce zalamují řádek. */
const LINE_BREAKING_TAGS = "p, div, ul, ol, li";

/** Čím se v jednořádkovém režimu nahradí zlom řádku. */
const LINE_SEPARATOR = " · ";

/**
 * Ověří a znormalizuje adresu odkazu. Vrací null, pokud adresa není použitelná
 * nebo používá nebezpečné schéma (javascript:, data: apod.).
 */
export function normalizeUrl(value: string | null | undefined): string | null {
  const raw = (value ?? "").trim();
  if (raw == "") {
    return null;
  }

  // Bez schématu nelze rozhodnout, jde-li o web nebo e-mail - doplníme podle tvaru.
  let candidate = raw;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(candidate)) {
    candidate = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(candidate)
      ? "mailto:" + candidate
      : "https://" + candidate;
  }

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }

  if (!ALLOWED_SCHEMES.includes(parsed.protocol.toLowerCase())) {
    return null;
  }

  return parsed.href;
}

/** Odstraní z prvku všechny atributy. */
function stripAttributes(element: Element) {
  // Kolekce je živá, proto kopie názvů předem.
  for (const name of Array.from(element.attributes).map((a) => a.name)) {
    element.removeAttribute(name);
  }
}

/** Vymění prvek za stejný obsah v jiné značce a vrátí novou značku. */
function renameElement(element: Element, tagName: string): Element {
  const parent = element.parentNode;
  if (parent == null) {
    return element;
  }

  const replacement = element.ownerDocument.createElement(tagName);
  while (element.firstChild != null) {
    replacement.appendChild(element.firstChild);
  }

  parent.replaceChild(replacement, element);

  return replacement;
}

/** Nahradí prvek jeho vlastním obsahem. */
function unwrap(element: Element) {
  const parent = element.parentNode;
  if (parent == null) {
    return;
  }

  while (element.firstChild != null) {
    parent.insertBefore(element.firstChild, element);
  }

  parent.removeChild(element);
}

/** Projde podstrom a vynutí allowlist značek i atributů. */
function cleanNode(node: Node) {
  // Kopie, protože se seznam dětí během úklidu mění.
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType == Node.TEXT_NODE) {
      continue;
    }

    if (child.nodeType != Node.ELEMENT_NODE) {
      child.parentNode?.removeChild(child);
      continue;
    }

    let element = child as Element;
    let tag = element.tagName.toUpperCase();

    if (DROPPED_TAGS.includes(tag)) {
      element.parentNode?.removeChild(element);
      continue;
    }

    const alias = TAG_ALIASES[tag];
    if (alias != null) {
      element = renameElement(element, alias);
      tag = element.tagName.toUpperCase();
    }

    cleanNode(element);

    if (!ALLOWED_TAGS.includes(tag)) {
      unwrap(element);
      continue;
    }

    if (tag != "A") {
      stripAttributes(element);
      continue;
    }

    const href = normalizeUrl(element.getAttribute("href"));
    stripAttributes(element);

    // Odkaz bez použitelné adresy nemá smysl - necháme jen jeho text.
    if (href == null) {
      unwrap(element);
      continue;
    }

    element.setAttribute("href", href);
    element.setAttribute("target", "_blank");
    element.setAttribute("rel", "noopener noreferrer");
  }
}

/**
 * Vyčistí HTML poznámky na povolenou podmnožinu. Musí proběhnout před každým
 * vložením obsahu do v-html i před uložením na server.
 */
export function sanitizeNoteHtml(value: string | null | undefined): string {
  const raw = value ?? "";
  if (raw.trim() == "") {
    return "";
  }

  // DOMParser nemá browsing context, takže se nic nespustí ani nenačte.
  const document = new DOMParser().parseFromString(raw, "text/html");
  cleanNode(document.body);

  return document.body.innerHTML;
}

/**
 * Pozná, zda hodnota už obsahuje formátování. Poznámky uložené před zavedením
 * editoru jsou čistý text a musí se zobrazit jinak (escape + zachování řádků).
 */
export function isNoteHtml(value: string | null | undefined): boolean {
  const raw = value ?? "";

  // Lomítko v <br/> a entity musí projít taky: editor vyrábí &nbsp; i v obsahu,
  // kde žádná značka není (stačí napsat dvě mezery). Bez toho by se takový
  // obsah vyhodnotil jako starý čistý text a entita by se zobrazila doslova.
  return (
    /<\/?(?:b|strong|i|em|u|s|strike|del|a|br|p|div|ul|ol|li)(?:\s[^>]*)?\/?>/i.test(
      raw
    ) || /&(?:[a-z]+|#\d+);/i.test(raw)
  );
}

/** Převede čistý text na HTML - escapuje znaky a zachová zlomy řádků. */
export function plainTextToNoteHtml(value: string | null | undefined): string {
  const raw = value ?? "";
  if (raw == "") {
    return "";
  }

  const container = document.createElement("div");
  container.textContent = raw;

  return container.innerHTML.replace(/\r?\n/g, "<br>");
}

/**
 * Připraví uloženou hodnotu k zobrazení nebo k editaci. Zvládne jak nové
 * formátované poznámky, tak starý čistý text.
 */
export function toNoteHtml(value: string | null | undefined): string {
  return isNoteHtml(value)
    ? sanitizeNoteHtml(value)
    : plainTextToNoteHtml(value);
}

/**
 * Vytáhne z poznámky čistý text. Používá se tam, kde není místo na formátování
 * (tooltip na dispečerské tabuli).
 */
export function noteHtmlToPlainText(value: string | null | undefined): string {
  const raw = value ?? "";
  if (raw.trim() == "") {
    return "";
  }

  if (!isNoteHtml(raw)) {
    return raw;
  }

  const document_ = new DOMParser().parseFromString(raw, "text/html");
  for (const element of Array.from(document_.body.querySelectorAll("*"))) {
    if (DROPPED_TAGS.includes(element.tagName.toUpperCase())) {
      element.parentNode?.removeChild(element);
    }
  }

  // Blokové značky by jinak slepily slova k sobě.
  for (const element of Array.from(
    document_.body.querySelectorAll("br, p, div, li")
  )) {
    element.parentNode?.insertBefore(document_.createTextNode(" "), element);
  }

  return (document_.body.textContent ?? "").replace(/\s+/g, " ").trim();
}

/** Pozná, zda poznámka po odstranění formátování nic neobsahuje. */
export function isNoteEmpty(value: string | null | undefined): boolean {
  return noteHtmlToPlainText(value) == "";
}

/** Text, který v poznámce stojí před daným uzlem. */
function textBefore(root: Element, node: Node): string {
  const range = root.ownerDocument.createRange();
  range.setStart(root, 0);
  range.setEndBefore(node);

  return range.toString();
}

/** Text, který v poznámce stojí za daným uzlem. */
function textAfter(root: Element, node: Node): string {
  const range = root.ownerDocument.createRange();
  range.setStartAfter(node);
  range.setEnd(root, root.childNodes.length);

  return range.toString();
}

/**
 * Potřebuje uzel před sebou oddělovač? Nepotřebuje na začátku poznámky ani
 * tam, kde už oddělovač stojí - zlomy se běžně sejdou (prázdný řádek, seznam
 * hned za odstavcem) a dva oddělovače za sebou by vypadaly jako chyba.
 */
function needsLineSeparator(root: Element, node: Node): boolean {
  const before = textBefore(root, node);

  return before.trim() != "" && !before.endsWith(LINE_SEPARATOR);
}

/**
 * Zploští poznámku na jeden řádek a formátování přitom zachová. Zlomy řádků se
 * vymění za oddělovač, protože blokové značky ani <br> se nedají potlačit
 * stylem - <br> zalomí řádek i pod white-space: nowrap.
 *
 * Pro pruhy a řádky, kde je na poznámku jen jeden řádek, ale formátování se
 * hodí (poznámka slotu na dispečerské tabuli). Když formátování zobrazit
 * nelze, patří místo toho noteHtmlToPlainText().
 */
export function noteHtmlToSingleLine(value: string | null | undefined): string {
  const html = autoLinkNoteHtml(toNoteHtml(value));
  if (html.trim() == "") {
    return "";
  }

  const document_ = new DOMParser().parseFromString(html, "text/html");
  const body = document_.body;

  for (const br of Array.from(body.querySelectorAll("br"))) {
    if (needsLineSeparator(body, br)) {
      br.parentNode?.replaceChild(document_.createTextNode(LINE_SEPARATOR), br);
    } else {
      br.parentNode?.removeChild(br);
    }
  }

  // Seznam se rozbalí před svými položkami, proto pořadí v dokumentu.
  for (const block of Array.from(body.querySelectorAll(LINE_BREAKING_TAGS))) {
    if (needsLineSeparator(body, block)) {
      block.parentNode?.insertBefore(
        document_.createTextNode(LINE_SEPARATOR),
        block
      );
    }

    unwrap(block);
  }

  // Poznámka končící prázdným řádkem by za sebou nechala visící oddělovač.
  const walker = document_.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  const texts: Text[] = [];
  while (walker.nextNode() != null) {
    texts.push(walker.currentNode as Text);
  }

  for (const text of texts.reverse()) {
    if (
      (text.textContent ?? "").trim() == LINE_SEPARATOR.trim() &&
      textAfter(body, text).trim() == ""
    ) {
      text.parentNode?.removeChild(text);
    }
  }

  return document_.body.innerHTML;
}

/**
 * Udělá z holých URL a e-mailů v textu klikatelné odkazy. Text uvnitř
 * stávajícího odkazu se nechává být, aby nedošlo k zanoření.
 */
export function autoLinkNoteHtml(value: string | null | undefined): string {
  const raw = value ?? "";
  if (raw.trim() == "") {
    return "";
  }

  const document_ = new DOMParser().parseFromString(raw, "text/html");
  const walker = document_.createTreeWalker(
    document_.body,
    NodeFilter.SHOW_TEXT
  );

  const textNodes: Text[] = [];
  while (walker.nextNode() != null) {
    const node = walker.currentNode as Text;
    if (node.parentElement?.closest("a") == null) {
      textNodes.push(node);
    }
  }

  for (const node of textNodes) {
    const text = node.textContent ?? "";
    AUTOLINK_PATTERN.lastIndex = 0;
    if (!AUTOLINK_PATTERN.test(text)) {
      continue;
    }

    const fragment = document_.createDocumentFragment();
    let lastIndex = 0;

    AUTOLINK_PATTERN.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = AUTOLINK_PATTERN.exec(text)) != null) {
      // Koncová interpunkce patří větě, ne adrese.
      const trailing = TRAILING_PUNCTUATION.exec(match[0]);
      const matched =
        trailing == null
          ? match[0]
          : match[0].slice(0, match[0].length - trailing[0].length);

      const href = normalizeUrl(matched);
      if (matched == "" || href == null) {
        continue;
      }

      fragment.appendChild(
        document_.createTextNode(text.slice(lastIndex, match.index))
      );

      const link = document_.createElement("a");
      link.setAttribute("href", href);
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
      link.textContent = matched;
      fragment.appendChild(link);

      lastIndex = match.index + matched.length;
    }

    fragment.appendChild(document_.createTextNode(text.slice(lastIndex)));
    node.parentNode?.replaceChild(fragment, node);
  }

  return document_.body.innerHTML;
}
